import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Container, Box, Typography, CircularProgress } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout/Layout';
import Login from './components/Auth/Login';

// Route-level code splitting: previously every one of these - including
// admin-only pages like Security Groups, User Management, and Audit Log -
// was bundled into the single initial JS chunk every authenticated user
// downloads on login, whether they ever visit that page or not.
const Dashboard = lazy(() => import('./components/Dashboard/Dashboard'));
const AssessmentList = lazy(() => import('./components/Assessments/AssessmentList'));
const StandardsList = lazy(() => import('./components/Standards/StandardsList'));
const AuditLogComponent = lazy(() => import('./components/Audit/AuditLog'));
const SecurityGroups = lazy(() => import('./components/Security/SecurityGroups'));
const UserManagement = lazy(() => import('./components/Security/UserManagement'));
const HeadsOfSubject = lazy(() => import('./components/Staff/HeadsOfSubject'));
const CalendarExport = lazy(() => import('./components/Calendar/CalendarExport'));

const RouteLoadingFallback: React.FC = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
    <CircularProgress />
  </Box>
);

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});


const AuthCallback: React.FC = () => {
  const { handleAuthCallback } = useAuth();

  useEffect(() => {
    handleAuthCallback();
  }, [handleAuthCallback]);

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Completing authentication...
        </Typography>
      </Box>
    </Container>
  );
};

const AppRoutes: React.FC = () => {
  const { isAuthenticated } = useAuth();

  // Check if we're on the root path with auth parameters
  const urlParams = new URLSearchParams(window.location.search);
  const authSuccess = urlParams.get('auth_success');
  const authCode = urlParams.get('authCode');
  const error = urlParams.get('error');

  // If we have auth parameters on the root path, show the callback component
  if ((authSuccess || authCode || error) && window.location.pathname === '/') {
    return <AuthCallback />;
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="*" element={<Navigate to="/login" />} />
      </Routes>
    );
  }

  return (
    <Layout>
      <Suspense fallback={<RouteLoadingFallback />}>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/assessments" element={<AssessmentList />} />
          <Route path="/calendar-export" element={<CalendarExport />} />
          <Route path="/standards" element={<StandardsList />} />
          <Route path="/heads-of-subject" element={<HeadsOfSubject />} />
          <Route path="/audit" element={<AuditLogComponent />} />
          <Route path="/security-groups" element={<SecurityGroups />} />
          <Route path="/user-management" element={<UserManagement />} />
          <Route path="/login" element={<Navigate to="/" />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Suspense>
    </Layout>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <AppRoutes />
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
